import {
	createContext,
	FC,
	Fragment,
	ReactNode,
	useContext,
	useState,
} from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { LoadingOutlined } from '@ant-design/icons';
import {
	getFRMSiteReportDetail,
	submitFRMSiteReportBusinessActivity,
	submitFRMSiteReportDetail,
	submitFRMSiteReportUnitAnalysis,
} from '@/libs/fleet-api/services/visit-reports';
import MainInformation from '@/components/MainInformation';
import { Button, Divider, Form, Input, Result, Space, Tabs } from 'antd';
import FleetTitle from '@/libs/antd/components/Title';
import FRMSiteVisitReportDetail from './SiteReportPage.Detail';
import { useFRMSiteVisitReportDetailForm } from './SiteReportPage.Detail.Form';
import rule from '@/libs/antd/rules';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { SiteReport } from '@/libs/fleet-api/services/types';
import FRMSiteVisitReportPhotos from './SiteReportPage.Photo';
import FRMSiteVisitReportUnit, {
	useFRMSiteVisitReportUnitForm,
} from './SiteReportPage.Unit';
import FRMSiteVisitReportActivity, {
	useFRMSiteVisitReportActivityForm,
} from './SiteReportPage.Activity';
import FleetModal from '@/libs/antd/components/Modal';
import { useApp } from '@/App';
import GenericErrorMessage from '@/components/GenericErrorMessage';
import { useModals } from '@/components/modals/ModalProvider';

type TabKey = 'detail' | 'unit' | 'activity';

type PageContext = null | {
	query: ReturnType<typeof useGetFRMSiteReportDetail>;
	siteReportDetailForm: ReturnType<typeof useFRMSiteVisitReportDetailForm>;
	siteReportUnitForm: ReturnType<typeof useFRMSiteVisitReportUnitForm>;
	siteReportActivityForm: ReturnType<typeof useFRMSiteVisitReportActivityForm>;
};

const pageContext = createContext<PageContext>(null);
const { Provider } = pageContext;

export const useFRMSiteVisitReportPage = () => useContext(pageContext)!;

export const useGetFRMSiteReportDetail = (prospectId: string) => {
	return useQuery({
		queryKey: ['getFRMSiteReportDetail', prospectId],
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getFRMSiteReportDetail(id);
		},
	});
};

const FRMSiteVisitReportPage: FC<{ prospectId: string }> = ({ prospectId }) => {
	const { navigate } = useApp();
	const { openLKPDFModal } = useModals();
	const [activeTab, setActiveTab] = useState<TabKey>('detail');
	const query = useGetFRMSiteReportDetail(prospectId);
	const { data, isLoading, isFetching, isError } = query;
	const {
		mainInformation,
		surveyInformation,
		customerBusinessDocuments,
		customerLegalDocuments,
	} = data?.data ?? {};

	const [showConfirm, setShowConfirm] = useState(false);
	const [showSuccess, setShowSuccess] = useState(false);

	const siteReportDetailForm = useFRMSiteVisitReportDetailForm();
	const submitFRMSiteReportDetailMutation = useMutation({
		mutationFn: submitFRMSiteReportDetail,
	});

	const handleSubmitDetail = async (
		values: Parameters<typeof submitFRMSiteReportDetail>[0],
	) => {
		try {
			await submitFRMSiteReportDetailMutation.mutateAsync(values);
			setActiveTab('unit');
		} catch (error) {
			console.log(error);
		}
	};

	const siteReportUnitForm = useFRMSiteVisitReportUnitForm();
	const submitFRMSiteReportUnitAnalysisMutation = useMutation({
		mutationFn: submitFRMSiteReportUnitAnalysis,
	});

	const handleSubmitUnit = async (
		values: Parameters<typeof submitFRMSiteReportUnitAnalysis>[0],
	) => {
		try {
			await submitFRMSiteReportUnitAnalysisMutation.mutateAsync(values);
			setActiveTab('activity');
		} catch (error) {
			console.log(error);
		}
	};

	const siteReportActivityForm = useFRMSiteVisitReportActivityForm();
	const submitFRMSiteReportBusinessActivityMutation = useMutation({
		mutationFn: submitFRMSiteReportBusinessActivity,
	});

	const handleSubmitActivity = async (
		values: Parameters<typeof submitFRMSiteReportBusinessActivity>[0],
	) => {
		try {
			await submitFRMSiteReportBusinessActivityMutation.mutateAsync(values);
			setShowConfirm(false);
			setShowSuccess(true);
		} catch (error) {
			setShowConfirm(false);
			console.log(error);
		}
	};

	if (isError) return <GenericErrorMessage />;

	return (
		<Fragment>
			<MainInformation
				mainInformation={mainInformation}
				surveyInformation={surveyInformation}
				customerBusinessDocuments={customerBusinessDocuments}
				customerLegalDocuments={customerLegalDocuments}
				isLoading={isFetching}
			/>
			<Divider />
			{isLoading ? (
				<LoadingOutlined
					style={{
						marginTop: 160,
						fontSize: 100,
						color: '#1557BC',
						display: 'block',
					}}
				/>
			) : (
				<Provider
					value={{
						query,
						siteReportDetailForm,
						siteReportUnitForm,
						siteReportActivityForm,
					}}
				>
					<FleetTitle>Laporan Kunjungan On Site</FleetTitle>
					<Form.Provider
						onFormFinish={async (name, { values }) => {
							if (!name) return;
							handleSubmitDetail(values as SiteReport<'payload'>);
						}}
					>
						<Tabs
							activeKey={activeTab}
							onChange={(key) => setActiveTab(key as TabKey)}
							items={[
								{
									key: 'detail',
									label: 'Detail Kunjungan',
									children: <FRMSiteVisitReportDetail />,
								},
								{
									key: 'unit',
									label: 'Analisa Unit',
									children: (
										<FRMSiteVisitReportUnit onSubmit={handleSubmitUnit} />
									),
								},
								{
									key: 'activity',
									label: 'Aktivitas Usaha',
									children: (
										<FRMSiteVisitReportActivity
											onSubmit={handleSubmitActivity}
										/>
									),
								},
							]}
						/>

						<FleetTitle>Action Plan</FleetTitle>
						<Form
							{...ANTD_DEFAULT_PROPS.FORM}
							name="siteReport"
							form={siteReportDetailForm.form}
							style={{ marginBottom: 20 }}
						>
							<Form.Item
								name={siteReportDetailForm.fieldName('actionPlan')}
								rules={[rule.required('Action Plan wajib diisi')]}
							>
								<Input.TextArea rows={4} />
							</Form.Item>
						</Form>

						<FRMSiteVisitReportPhotos />

						<Space
							style={{
								display: 'flex',
								justifyContent: 'flex-end',
								marginTop: 40,
							}}
							size="large"
						>
							{
								(
									{
										detail: (
											<Fragment key="detail">
												<Button
													type="primary"
													style={{
														width: 200,
														display: 'block',
														marginLeft: 'auto',
													}}
													loading={submitFRMSiteReportDetailMutation.isPending}
													onClick={() => {
														siteReportDetailForm.form.submit();
													}}
												>
													NEXT
												</Button>
											</Fragment>
										),
										unit: (
											<Fragment key="unit">
												<Button
													type="primary"
													style={{
														width: 200,
														display: 'block',
														marginLeft: 'auto',
													}}
													loading={
														submitFRMSiteReportUnitAnalysisMutation.isPending
													}
													onClick={() => {
														siteReportUnitForm.form.submit();
													}}
												>
													NEXT
												</Button>
											</Fragment>
										),
										activity: (
											<Fragment key="activity">
												<Button
													type="default"
													onClick={() => {
														openLKPDFModal({
															type: 'lk-site',
															prospectId: mainInformation!.prospectId!,
														});
													}}
												>
													DOWNLOAD LK SITE
												</Button>
												<Button
													type="primary"
													style={{
														width: 200,
														display: 'block',
														marginLeft: 'auto',
													}}
													loading={
														submitFRMSiteReportBusinessActivityMutation.isPending
													}
													onClick={() => setShowConfirm(true)}
												>
													SAVE
												</Button>
											</Fragment>
										),
									} as Record<TabKey, ReactNode>
								)[activeTab]
							}
						</Space>

						<FleetModal
							open={Boolean(showConfirm)}
							styles={{ footer: { padding: 0 } }}
							okText="YA"
							cancelText="TIDAK"
							onCancel={() => setShowConfirm(false)}
							onOk={() => {
								siteReportActivityForm.form.submit();
							}}
							okButtonProps={{
								loading: submitFRMSiteReportBusinessActivityMutation.isPending,
							}}
						>
							<Result
								status="info"
								subTitle={
									<strong style={{ color: '#2F2F2F' }}>
										Apakah Anda yakin ingin menyimpan data ini? <br />
										Pastikan semua informasi sudah benar sebelum melanjutkan
									</strong>
								}
							/>
						</FleetModal>

						<FleetModal
							open={Boolean(showSuccess)}
							styles={{ footer: { padding: 0 } }}
							cancelButtonProps={{
								hidden: true,
							}}
							okText="NEXT"
							onOk={() => {
								setShowSuccess(false);
								navigate('FRMVisitReportListPage');
							}}
						>
							<Result
								status="success"
								title="Data Laporan Kunjungan Anda telah tersimpan"
								subTitle={
									<p>
										Silakan lanjutkan mengisi Laporan Kunjungan Lainnya atau
										Request Approval
									</p>
								}
							/>
						</FleetModal>
					</Form.Provider>
				</Provider>
			)}
		</Fragment>
	);
};

export default FRMSiteVisitReportPage;
